// src/App.jsx
// Shell only — layout, mode state, global sheet state.
// No business logic. No inline components. No nav data.
//
// To add a route:    edit src/shell/Routes.jsx
// To change nav:     edit src/shell/nav.js
// To change styles:  edit src/shell/GlobalStyles.jsx
// To change header:  edit src/shell/Header.jsx
// To change nav bar: edit src/shell/Nav.jsx

import { useLangStore } from '@store/lang';
import './utils/scenarioSimulator';
import { useSituationFeeder } from '@hooks/useSituationFeeder';
import { useAuthSync }        from '@hooks/useAuthSync';
import React, { useState, useEffect, useCallback } from 'react';
import { AppErrorBoundary as ErrorBoundary } from '@shell/ErrorBoundary';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAppStore } from '@store';
import { useOnlineStatus }  from '@hooks';
import { useConnectivityState } from '@hooks/useConnectivityState';
import { useCacheWarmer, useChunkPrewarmer } from '@hooks/useCacheWarmer';
import { useLowPowerMode }  from '@hooks/useLowPowerMode';
import { useDisasterMode }      from '@hooks/useDisasterMode';
import { useSituation }          from '@hooks/useSituation';
const LazyDisasterBanner = React.lazy(() =>
  import('@features/disaster/DisasterBanner').then(m => ({ default: m.DisasterBanner }))
);
import { useAccessibilityStore } from '@store/accessibilityStore';
import { useVoiceAlerts }        from '@hooks/useVoiceAlerts';
import { T } from '@components/theme';
import { CriticalAlertBanner } from '@features/alerts/CriticalAlertBanner';

import { GlobalStyles }               from '@shell/GlobalStyles';
import { useSituationStore } from '@store/situationStore'; import { selectSourceHealth } from '@store/situationStore';
import { Header }                     from '@shell/Header';
import { IOSInstallPrompt } from '@shell/IOSInstallPrompt';
import { SubNav, BottomNav }          from '@shell/Nav.jsx';
import { AppRoutes, SplashRoute }     from '@shell/Routes';
import { PATH_TO_MODE }               from '@shell/nav';

import { AlertBar, AlertDetailSheet } from '@features/alerts/AlertBar';
import { usePushNotifications }              from '@hooks/usePushNotifications';
import { EmergencyAlertModal }               from '@features/alerts/PushNotifications';
import { ShareSheet }                 from '@features/more/ShareSheet';
import { DisclaimerGate, hasAcceptedDisclaimer } from '@features/legal/DisclaimerGate';
import { PersistentDisclaimerBar } from '@features/legal/LegalComponents';
import { OfflineBanner }             from '@shell/OfflineBanner';
const PanicScreen = React.lazy(() => import('@features/panic/PanicScreen'));
// DisasterBanner is loaded lazily via React.lazy wrapper below
import { prefetchAreaTiles }        from '@services/maps/tileCache';
import { ZONE_CENTERS }             from '@data/zoneCenters';

export default function App() {
  const zone    = useAppStore((s) => s.zone);
  const setZone = useAppStore((s) => s.setZone);
  const online  = useOnlineStatus();
  const { backendState, sourceState } = useConnectivityState();
  const sourceHealth = useSituationStore(selectSourceHealth('all'));
  const nwsStale  = sourceHealth?.nws?.status === 'stale' || sourceHealth?.nws?.status === 'unavailable';
  const { lang } = useLangStore();
  useSituationFeeder(); // Feed reconciliation engine at app startup
  useAuthSync();        // Sync auth state + zones to Firestore on sign-in

  const [disclaimerAccepted, setDisclaimerAccepted] = useState(hasAcceptedDisclaimer);
  const [panicMode,          setPanicMode]          = useState(false);

  useCacheWarmer();  // warms all critical endpoints when app comes to foreground
  useChunkPrewarmer(); // preloads lazy route chunks for offline use
  const lpm      = useLowPowerMode();

  // ── User presence tracking ─────────────────────────────────────
  // Writes {lastSeenAt, currentPath} to user_presence/{uid}
  // so the Command Center can show online/offline/map/radio counts.
  // Only runs if the user is authenticated.
  React.useEffect(() => {
    let unsub = null;
    let hb    = null;   // heartbeat interval — cleared in effect cleanup below
    (async () => {
      try {
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        // Google redirect result handled in main.jsx after Firebase init
        const { getFirestore, doc, setDoc }   = await import('firebase/firestore');
        const { getApp }  = await import('firebase/app');
        const auth = getAuth(getApp());
        const db   = getFirestore(getApp());
        unsub = onAuthStateChanged(auth, async user => {
          if (!user) {
            // Session lost (token expired, cleared storage, etc.)
            // Re-authenticate anonymously to restore a valid UID.
            // This is non-blocking — if it fails the app still works,
            // writes will fail silently until the next page load.
            try {
              const { signInAnonymously } = await import('firebase/auth');
              await signInAnonymously(auth);
            } catch (e) {
              console.warn('[Auth] Re-auth failed:', e.code);
            }
            return;
          }
          const ref = doc(db, 'user_presence', user.uid);
          // Update on mount
          setDoc(ref, { lastSeenAt: new Date().toISOString(), currentPath: window.location.pathname, radioMode: window.location.pathname === '/radio' }, { merge: true }).catch(() => {});
          // Update every 3 minutes (heartbeat).
          // Interval id stored in outer `hb` so the effect cleanup below
          // can clear it — returning from this callback has no effect.
          if (hb) clearInterval(hb);   // clear any previous interval if auth fires twice
          hb = setInterval(() => {
            setDoc(ref, { lastSeenAt: new Date().toISOString(), currentPath: window.location.pathname, radioMode: window.location.pathname === '/radio' }, { merge: true }).catch(() => {});
          }, 3 * 60_000);
        });
      } catch {}
    })();
      () => {
      unsub?.();
      if (hb) clearInterval(hb);   // guarantee interval is cleared on unmount
    };
  }, []);
  const disaster    = useDisasterMode();
  const situation   = useSituation();
  const a11y        = useAccessibilityStore();
  useVoiceAlerts();   // reads EXTREME/SEVERE alerts aloud when voiceAlerts is on

  // Apply CSS classes to <html> whenever any a11y setting changes
  React.useEffect(() => { a11y.applyToDOM(); },
    [a11y.largeText, a11y.highContrast, a11y.colorblindMode,
     a11y.reduceMotion, a11y.voiceAlerts]);  // eslint-disable-line

  // Voice alerts — read extreme/severe NWS alerts aloud
  const spokenAlerts = React.useRef(new Set());
  React.useEffect(() => {
    if (!a11y.voiceAlerts) return;
    if (!window.speechSynthesis) return;
    // Import alerts from store
    import('@store/alertStore').then(({ useAlertStore }) => {
      const alerts = useAlertStore.getState()?.nwsAlerts ?? [];
      const urgent = alerts.filter(a =>
        a.status === 'active' &&
        (a.severity === 'extreme' || a.severity === 'severe') &&
        !spokenAlerts.current.has(a.id)
      );
      for (const alert of urgent.slice(0, 2)) {
        spokenAlerts.current.add(alert.id);
        const utt = new SpeechSynthesisUtterance(
          `Emergency alert: ${alert.title}. ${alert.headline ?? ''}`
        );
        utt.rate = 0.9;
        utt.volume = 1;
        window.speechSynthesis.speak(utt);
      }
    }).catch(() => {});
  }, [a11y.voiceAlerts]);
  // Auto-enable low power mode when a disaster alert is active
  React.useEffect(() => { if (disaster.active) lpm.enable(); }, [disaster.active]);
  const push = usePushNotifications();
  const [mode,      setMode]      = useState('home');
  const [alertOpen, setAlertOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Keep mode indicator in sync with the active route
  useEffect(() => {
    const m = PATH_TO_MODE[location.pathname];
    if (m) setMode(m);
  }, [location.pathname]);

  const handleSetMode = useCallback((id) => setMode(id), []);

  // SEO pages bypass zone requirement — crawlable without zone selection
  const SEO_PATHS = ['/about', '/how-it-works', '/data-sources'];
  if (!zone && !SEO_PATHS.includes(window.location.pathname)) {
    return (
      <>
        <GlobalStyles />
        <SplashRoute onComplete={(z) => {
            setZone(z);
            navigate('/feed');
            // Pre-cache map tiles for the selected zone in the background.
            // Runs silently — user never sees it, but the map works offline.
            const center = ZONE_CENTERS[z];
            if (center && navigator.onLine) {
              prefetchAreaTiles(center.lat, center.lon, 8).catch(() => {});
            }
          }} />
      </>
    );
  }

  if (!disclaimerAccepted) {
    return <DisclaimerGate onAccept={() => setDisclaimerAccepted(true)} />;
  }

  return (
    <ErrorBoundary>
      <>
        <GlobalStyles />

        <div style={{
          display: 'flex', flexDirection: 'column',
          height: '100dvh', maxWidth: '100%',
          margin: '0 auto', background: T.bg,
          fontFamily: T.font, position: 'relative',
        }}>
        <Header zone={zone} online={online} onZonePress={() => navigate('/gps')} sourceStale={nwsStale} staleSourceName="NWS" backendState={backendState} sourceState={sourceState} />
        <AlertBar onTap={() => setAlertOpen(true)} />
        <OfflineBanner onPanicMode={() => setPanicMode(true)} />
        <CriticalAlertBanner />
        {/* Collapse SubNav during extreme emergencies to save vertical space */}
        {situation.level !== 'warning' && (
          <SubNav mode={mode} situationLevel={situation.level} />
        )}
        {disaster.active && (
          <React.Suspense fallback={null}>
            <LazyDisasterBanner />
          </React.Suspense>
        )}
        {/* Screen reader live region — EXTREME alerts announced automatically */}
        <div id="eos-alert-announce" aria-live="assertive" aria-atomic="true"
             style={{ position:'absolute', left:'-9999px', width:'1px', height:'1px', overflow:'hidden' }} />
        {panicMode && (
          <React.Suspense fallback={null}>
            <PanicScreen onClose={() => setPanicMode(false)} />
          </React.Suspense>
        )}
        <PersistentDisclaimerBar />
        {lang === 'es' && (
          <div style={{ background:'rgba(59,130,246,0.1)', borderBottom:'1px solid rgba(59,130,246,0.2)',
            padding:'5px 12px', fontSize:10, color:'#3b82f6', fontWeight:600, flexShrink:0,
            display:'flex', alignItems:'center', gap:6 }}>
            🌐 Modo Español activo · Contenido oficial de NWS permanece en inglés
          </div>
        )}

        <main id="main-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <AppRoutes onShareOpen={() => setShareOpen(true)} />
        </main>

        <BottomNav mode={mode} setMode={handleSetMode} />
        <IOSInstallPrompt />
      </div>

      {/* Global sheets rendered outside scroll container to prevent iOS clipping */}
      <AlertDetailSheet open={alertOpen} onClose={() => setAlertOpen(false)} />
      <ShareSheet       open={shareOpen} onClose={() => setShareOpen(false)} />
      <EmergencyAlertModal alert={push.emergency} onClose={push.clearEmergency} />
    </>
    </ErrorBoundary>
  );
}

// ── SW Update Banner (wired into shell) ───────────────────────────
// Import lazily so it doesn't affect initial load
// Usage: drop <SWUpdateBanner /> anywhere inside App
// export { SWUpdateBanner } from '@hooks/offline' via shell/UpdateBanner.jsx
